import { Box, Button, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type {
  ContinuousForecast,
  ContinuousTrend,
  MissedConversion,
  OpeningBookExit,
  OpponentChallengeBand,
  OutcomeOpportunityRate,
  PhaseErrorRate,
  PerformanceFormat,
  PlayerTrendsResponse,
  RateTrend,
  ScoreVersusExpectation,
  TimeUse,
  TrendSeriesPoint,
} from "@chessla/shared";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { layoutTokens } from "@/shared/theme";
import { ChesslaChart } from "./ChesslaChart";
import { TrendLine } from "./TrendLine";
import {
  buildActivityDistributionOption,
  buildActivityOption,
  buildChallengeOption,
  buildPhaseOption,
  buildRateOption,
  buildRepertoireOption,
  buildThinkPhaseOption,
  buildThinkTimeOption,
} from "./chart-options";
import { PositionThumb } from "@/shared/components/chessboard";
import { useTrends } from "./useTrends";
import { PerformanceSelection, usePerformanceSelection } from "@/features/performance-selection";

const percent = (value: number) => `${Math.round(value * 100)}%`;
const reason: Record<string, string> = {
  needs_20_games: "Needs at least 20 eligible games.",
  needs_8_games: "Needs at least 8 contributing games.",
  needs_4_dates: "Needs games on at least 4 dates.",
  needs_100_decisions: "Needs at least 100 analyzed decisions across 8 games.",
  no_eligible_analysis: "No compatible analyzed games in this scope.",
  clock_not_available: "Daily games do not provide comparable clock decisions.",
  needs_30_games: "Prediction needs at least 30 eligible games.",
  needs_6_weeks: "Prediction needs games across at least 6 distinct weeks.",
  calibration_failed: "The model did not meet the minimum held-out coverage check.",
};
const ForecastNote = ({ forecast }: { forecast?: ContinuousForecast }) =>
  !forecast ? null : forecast.status === "insufficient_data" ? (
    <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 12 }}>
      {reason[forecast.reason]} Current sample: {forecast.sampleSize} games across {forecast.distinctWeeks} weeks.
    </Typography>
  ) : (
    <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 12 }}>
      Dotted red: model expected path · shaded red: Nominal model range (not a guarantee) ·{" "}
      {percent(forecast.probabilityIncrease)} model-estimated probability of increase · Measured held-out coverage{" "}
      {percent(forecast.calibration.coverage)} ({forecast.calibration.holdoutSize} games). Future zig-zags are unknown,
      so the model predicts a distribution—not invented results.
    </Typography>
  );

export const trendDirectionGlyph = (direction: "improving" | "declining" | "uncertain") =>
  direction === "improving" ? "↗" : direction === "declining" ? "↘" : "";
export const TrendSignal = ({
  trend,
  unit,
  valueLabel = unit,
  forecast,
}: {
  trend: ContinuousTrend;
  unit: string;
  valueLabel?: string;
  forecast?: ContinuousForecast;
}) =>
  trend.status === "insufficient_data" ? (
    <Box>
      <Typography sx={{ fontSize: "2rem", fontWeight: 900 }}>Not enough evidence yet</Typography>
      <Typography sx={{ color: "text.secondary" }}>
        {reason[trend.reason]} Current sample: {trend.sampleSize} games on {trend.distinctDates} dates.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ py: 2, minWidth: 0 }}>
      <Typography
        sx={{
          color: trend.direction === "declining" ? "secondary.main" : "primary.main",
          fontSize: { xs: "2rem", sm: "3rem" },
          fontWeight: 900,
          letterSpacing: "-.05em",
          lineHeight: 1,
        }}
      >
        {trend.direction[0].toUpperCase()}
        {trend.direction.slice(1)}
        {trendDirectionGlyph(trend.direction) && (
          <Box component="span" aria-hidden sx={{ ml: 0.5 }}>
            {trendDirectionGlyph(trend.direction)}
          </Box>
        )}
      </Typography>
      <Typography sx={{ mt: 1, color: "text.secondary" }}>
        {trend.slopePer30Days >= 0 ? "+" : ""}
        {trend.slopePer30Days.toFixed(1)} {unit} per 30 days · 95% interval {trend.interval.low.toFixed(1)} to{" "}
        {trend.interval.high.toFixed(1)} · {trend.sampleSize} games
      </Typography>
      <Box sx={{ mt: 2, minWidth: 0 }}>
        <TrendLine
          series={trend.series}
          label={`${unit} observed over time`}
          valueLabel={valueLabel}
          forecast={forecast?.status === "observed" ? forecast : undefined}
        />
      </Box>
      <ForecastNote forecast={forecast} />
    </Box>
  );

export const RateSignal = ({ trend, label = "Error rate" }: { trend: RateTrend; label?: string }) =>
  trend.status === "insufficient_data" ? (
    <Box>
      <Typography sx={{ fontSize: "2rem", fontWeight: 900 }}>Not enough evidence yet</Typography>
      <Typography sx={{ color: "text.secondary" }}>
        {reason[trend.reason]} Current sample: {trend.games} games and {trend.decisions} decisions.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ minWidth: 0 }}>
      <ChesslaChart
        kind="posterior-rate"
        label={`${label}: earlier ${percent(trend.prior.mean)}, recent ${percent(trend.recent.mean)}, with 95 percent intervals.`}
        option={buildRateOption({ label, prior: trend.prior, recent: trend.recent })}
        height={240}
      />
      <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
        {trend.games} games · {trend.decisions} decisions · {percent(trend.probabilityImprovement)} probability the rate
        improved. Earlier 95% interval {percent(trend.prior.interval.low)}–{percent(trend.prior.interval.high)}; recent{" "}
        {percent(trend.recent.interval.low)}–{percent(trend.recent.interval.high)}.
      </Typography>
    </Box>
  );

const signedPoints = (value: number) => (value > 0 ? `+${value}` : String(value));
const expectationPosition = (low: number, high: number) =>
  low === 0 && high === 0
    ? "At baseline"
    : low > 0
      ? "Interval entirely above baseline"
      : high < 0
        ? "Interval entirely below baseline"
        : "Interval crosses baseline";
export const ResultsExpectationSignal = ({
  score,
}: {
  score: Extract<ScoreVersusExpectation, { status: "observed" }>;
}) => {
  const points = Math.round(score.difference * 100) || 0;
  const low = Math.round(score.interval.low * 100) || 0;
  const high = Math.round(score.interval.high * 100) || 0;
  const bound = Math.max(10, Math.ceil(Math.max(Math.abs(points), Math.abs(low), Math.abs(high)) / 5) * 5);
  const x = (value: number) => 30 + ((value + bound) / (bound * 2)) * 540;
  const position = expectationPosition(low, high);
  const observedPosition =
    points > 0 ? "Observed above baseline" : points < 0 ? "Observed below baseline" : "At baseline";
  const comparison =
    points > 0
      ? `${points} percentage points above expectation for this sample.`
      : points < 0
        ? `${Math.abs(points)} percentage points below expectation for this sample.`
        : "0 percentage points from expectation for this sample.";
  const summary = `Observed difference: ${signedPoints(points)} percentage points. 95% interval: ${signedPoints(low)} to ${signedPoints(high)} percentage points. ${position}.`;
  return (
    <Box sx={{ mt: 3, minWidth: 0 }}>
      <Typography sx={{ color: "secondary.main", fontSize: 11, fontWeight: 900, letterSpacing: ".12em" }}>
        SCORE VS ELO BASELINE
      </Typography>
      <Typography
        sx={{
          mt: 0.75,
          fontSize: { xs: "1.5rem", sm: "2rem" },
          fontWeight: 900,
          letterSpacing: "-.035em",
          lineHeight: 1.1,
        }}
      >
        You scored {percent(score.score)}; the Elo rating baseline was {percent(score.expectedScore)}.
      </Typography>
      <Typography sx={{ mt: 0.75, color: "text.secondary", fontWeight: 800 }}>{comparison}</Typography>
      <Box component="figure" data-chart-kind="expectation-residual" sx={{ m: 0, mt: 2, minWidth: 0 }}>
        <svg
          role="img"
          aria-label={summary}
          viewBox="0 0 600 105"
          width="100%"
          style={{ display: "block", maxHeight: 140 }}
        >
          <line x1="30" x2="570" y1="70" y2="70" stroke="currentColor" strokeOpacity=".35" strokeWidth="2" />
          <line x1="300" x2="300" y1="34" y2="100" stroke="#ff3d00" strokeWidth="3" />
          <line x1={x(low)} x2={x(high)} y1="70" y2="70" stroke="currentColor" strokeWidth="8" />
          <line x1={x(low)} x2={x(low)} y1="56" y2="84" stroke="currentColor" strokeWidth="3" />
          <line x1={x(high)} x2={x(high)} y1="56" y2="84" stroke="currentColor" strokeWidth="3" />
          <path d={`M ${x(points)} 52 l 18 18 -18 18 -18 -18 Z`} fill="#ff3d00" stroke="currentColor" strokeWidth="3" />
        </svg>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            color: "text.secondary",
            fontSize: { xs: 9, sm: 10 },
            fontWeight: 900,
            letterSpacing: ".04em",
            lineHeight: 1.1,
          }}
        >
          <Box component="span">
            BELOW
            <br />
            BASELINE
          </Box>
          <Box component="span" sx={{ textAlign: "center" }}>
            ELO
            <br />
            BASELINE
          </Box>
          <Box component="span" sx={{ textAlign: "right" }}>
            ABOVE
            <br />
            BASELINE
          </Box>
        </Box>
        <Typography component="figcaption" sx={{ display: "grid", gap: 0.35, color: "text.secondary", fontSize: 12 }}>
          <Box component="span" sx={{ color: "text.primary", fontWeight: 900 }}>
            {observedPosition} · {position}
          </Box>
          <Box component="span">
            95% interval: {signedPoints(low)} to {signedPoints(high)} percentage points · {score.games} games
          </Box>
          <Box component="span">
            Baseline estimates expected score from opponent Elo rating gaps; it does not grade move quality.
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export const buildActivityWeeks = (series: TrendSeriesPoint[]) => {
  const weeks = new Map<string, number>();
  for (const point of series) {
    const start = new Date(point.playedAt);
    start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
    start.setUTCHours(0, 0, 0, 0);
    const key = start.toISOString();
    weeks.set(key, (weeks.get(key) ?? 0) + 1);
  }
  return [...weeks].map(([weekStart, games]) => ({ weekStart, games }));
};
const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
const DAYPARTS = [
  { label: "NIGHT", detail: "00–06 UTC" },
  { label: "MORNING", detail: "06–12 UTC" },
  { label: "AFTERNOON", detail: "12–18 UTC" },
  { label: "EVENING", detail: "18–24 UTC" },
] as const;
export const buildActivityBreakdown = (series: TrendSeriesPoint[]) => {
  const weekdays = WEEKDAYS.map((label) => ({ label, games: 0 }));
  const dayparts = DAYPARTS.map((part) => ({ ...part, games: 0 }));
  for (const point of series) {
    const playedAt = new Date(point.playedAt);
    weekdays[(playedAt.getUTCDay() + 6) % 7].games += 1;
    dayparts[Math.floor(playedAt.getUTCHours() / 6)].games += 1;
  }
  return { weekdays, dayparts };
};
export const ActivityRhythm = ({ series }: { series: TrendSeriesPoint[] }) => {
  const weeks = buildActivityWeeks(series);
  if (!weeks.length) return <Typography sx={{ color: "text.secondary" }}>No activity in this scope.</Typography>;
  const breakdown = buildActivityBreakdown(series);
  return (
    <Box sx={{ minWidth: 0 }}>
      <ChesslaChart
        kind="activity-calendar"
        label="Weekly game activity; intensity is games played, not strength."
        option={buildActivityOption(weeks)}
        height={230}
      />
      <Box
        sx={{
          mt: 3,
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2,minmax(0,1fr))" },
          gap: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900 }}>Weekday rhythm</Typography>
          <ChesslaChart
            kind="weekday-activity"
            label="Games played by UTC weekday."
            option={buildActivityDistributionOption("Games by UTC weekday", breakdown.weekdays)}
            height={220}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900 }}>Time of day</Typography>
          <ChesslaChart
            kind="daypart-activity"
            label="Games played by UTC time of day."
            option={buildActivityDistributionOption("Games by UTC time of day", breakdown.dayparts)}
            height={220}
          />
        </Box>
      </Box>
    </Box>
  );
};
export const OpponentChallengeSignal = ({ bands }: { bands: OpponentChallengeBand[] }) =>
  bands.length ? (
    <Box sx={{ mt: 5, minWidth: 0 }}>
      <Typography sx={{ mb: 1, fontWeight: 900 }}>Opponent challenge mix</Typography>
      <ChesslaChart
        kind="challenge-distribution"
        label="Share of games against stronger, similar, and lower-rated opponents."
        option={buildChallengeOption(bands)}
        height={220}
      />
      <Box sx={{ display: "grid", gap: 0.5 }}>
        {bands.map((band) => (
          <Typography key={band.band} sx={{ color: "text.secondary", fontSize: 12 }}>
            <Box component="span" sx={{ color: "primary.main", fontWeight: 900 }}>
              {band.band === "lower" ? "LOWER-RATED" : `${band.band.toUpperCase()} OPPONENTS`}
            </Box>{" "}
            · {band.games} games · {percent(band.score)} score · {band.averageRatingGap >= 0 ? "+" : ""}
            {band.averageRatingGap} average gap
          </Typography>
        ))}
      </Box>
    </Box>
  ) : (
    <Typography sx={{ color: "text.secondary" }}>No opponent-rating evidence in this scope.</Typography>
  );
export const PhaseDecisionSignal = ({ phases }: { phases: PhaseErrorRate[] }) =>
  phases.length ? (
    <Box sx={{ minWidth: 0 }}>
      <ChesslaChart
        kind="phase-heatmap"
        label="Error rate by game phase."
        option={buildPhaseOption(phases)}
        height={220}
      />
      <Box sx={{ display: "grid", gap: 0.5 }}>
        {phases.map((phase) => (
          <Typography key={phase.phase} sx={{ color: "text.secondary", fontSize: 12 }}>
            <Box component="span" sx={{ color: "primary.main", fontWeight: 900 }}>
              {phase.phase.toUpperCase()}
            </Box>{" "}
            · {phase.rate ? percent(phase.rate.mean) : "unavailable"} · {phase.decisions} decisions across {phase.games}{" "}
            games
            {phase.rate
              ? ` · 95% interval ${percent(phase.rate.interval.low)}–${percent(phase.rate.interval.high)}`
              : ""}
          </Typography>
        ))}
      </Box>
    </Box>
  ) : (
    <Typography sx={{ color: "text.secondary" }}>No phase evidence in this scope.</Typography>
  );
const cardGridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
} as const;
const cardSx = {
  border: 1,
  borderColor: "divider",
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  p: 1.5,
} as const;
const cardLabelSx = { color: "primary.main", fontSize: 12, fontWeight: 900, letterSpacing: "0.04em" } as const;
const moveHref = (gameId: string, moveNumber: number, san: string | null) =>
  san ? `/games/${gameId}?move=${moveNumber}&san=${encodeURIComponent(san)}` : `/games/${gameId}`;

export const TimeUseSignal = ({ timeUse }: { timeUse: TimeUse }) => {
  if (timeUse.status === "insufficient_data")
    return (
      <Typography sx={{ color: "text.secondary" }}>
        {timeUse.reason === "clock_not_available"
          ? reason.clock_not_available
          : `Needs at least 100 analyzed decisions with clock data. Current sample: ${timeUse.decisions}/100 timed decisions.`}
      </Typography>
    );
  const worst = [...timeUse.bands].sort((a, b) => (b.errorRate?.mean ?? -1) - (a.errorRate?.mean ?? -1))[0];
  const worstLabel = { instant: "quick moves", normal: "normal thinks", long: "long thinks" }[worst.band];
  return (
    <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
      <Typography sx={{ color: "text.secondary" }}>
        Your typical think is {Math.round(timeUse.medianSeconds)}s ({timeUse.decisions} timed moves).
        {worst.errorRate ? ` Most mistakes per move come from ${worstLabel}: ${percent(worst.errorRate.mean)}.` : ""}
      </Typography>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "3fr 2fr" } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={cardLabelSx}>MISTAKE RATE BY THINK TIME</Typography>
          <ChesslaChart
            kind="think-time"
            label={`Mistake rate by think time: ${timeUse.bands
              .map((band) => `${band.band} ${band.errorRate ? percent(band.errorRate.mean) : "no moves"}`)
              .join(", ")}.`}
            option={buildThinkTimeOption(timeUse)}
            height={220}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={cardLabelSx}>AVERAGE THINK PER PHASE</Typography>
          <ChesslaChart
            kind="think-phase"
            label={`Average think per phase: ${timeUse.phases
              .flatMap((phase) =>
                phase.averageSeconds == null ? [] : [`${phase.phase} ${Math.round(phase.averageSeconds)}s`],
              )
              .join(", ")}.`}
            option={buildThinkPhaseOption(timeUse.phases)}
            height={150}
          />
        </Box>
      </Box>
      {timeUse.longThinkErrors.length > 0 && (
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 900 }}>Long thinks that still ended in a mistake</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {timeUse.longThinkErrors.map((error) => (
              <Button
                key={`${error.gameId}:${error.moveNumber}`}
                component={RouterLink}
                variant="outlined"
                size="small"
                to={moveHref(error.gameId, error.moveNumber, error.san)}
                sx={{ borderRadius: 0, textTransform: "none" }}
              >
                {error.secondsSpent}s on move {error.moveNumber}
                {error.san ? ` · ${error.san}` : ""} · {error.phase}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const pawns = (cp: number) => (cp === 0 ? "0.0" : `−${(cp / 100).toFixed(1)}`);
export const BookExitCard = ({
  opening,
  color,
  exit,
}: {
  opening: string;
  color: "white" | "black";
  exit: OpeningBookExit;
}) => (
  <Box component="article" aria-label={`${opening} as ${color}: where you leave theory`} sx={cardSx}>
    <Typography sx={cardLabelSx}>
      {color.toUpperCase()} · {opening}
    </Typography>
    <PositionThumb
      id={`book-exit-${color}-${exit.latestGameId}`}
      fen={exit.fen}
      orientation={color}
      played={exit.played[0]?.san ?? null}
      best={exit.bestMove}
    />
    <Typography variant="body2">
      You leave theory at move {exit.moveNumber} ({exit.games} games).
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      You played {exit.played.map((move) => `${move.san} ×${move.games} (${pawns(move.averageLossCp)})`).join(", ")}
      {exit.bestMove ? `. Best: ${exit.bestMove}.` : "."}
    </Typography>
    <Button
      component={RouterLink}
      size="small"
      to={moveHref(exit.latestGameId, exit.moveNumber, exit.latestSan)}
      sx={{ alignSelf: "flex-start" }}
    >
      Review latest game
    </Button>
  </Box>
);

export const MissedConversionList = ({ games }: { games: MissedConversion[] }) =>
  games.length ? (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ mb: 1, fontWeight: 900 }}>Winning positions you did not convert</Typography>
      <Box sx={cardGridSx}>
        {games.map(({ gameId, playedAt, result, playerColor, slip }) => (
          <Box component="article" key={gameId} aria-label={`Missed win from ${playedAt.slice(0, 10)}`} sx={cardSx}>
            <Typography sx={cardLabelSx}>
              {result.toUpperCase()}
              {slip ? ` · ${slip.phase.toUpperCase()}` : ""} · {playedAt.slice(0, 10)}
            </Typography>
            {slip?.fen && (
              <PositionThumb
                id={`missed-win-${gameId}`}
                fen={slip.fen}
                orientation={playerColor ?? "white"}
                played={slip.san}
                best={slip.bestMove}
              />
            )}
            <Typography variant="body2">
              {slip
                ? `Move ${slip.moveNumber}${slip.san ? ` ${slip.san}` : ""} at ${
                    slip.evalBeforeMate != null
                      ? `mate in ${Math.abs(slip.evalBeforeMate)}`
                      : `+${((slip.evalBeforeCp ?? 0) / 100).toFixed(1)}`
                  } cost ${(slip.lossCp / 100).toFixed(1)}${slip.bestMove ? ` · Best: ${slip.bestMove}` : ""}`
                : "No single move lost the advantage."}
            </Typography>
            <Button
              component={RouterLink}
              size="small"
              to={moveHref(gameId, slip?.moveNumber ?? 0, slip?.san ?? null)}
              sx={{ alignSelf: "flex-start" }}
            >
              Review this game
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;
const OutcomeRateSignal = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: OutcomeOpportunityRate;
  detail: string;
}) => (
  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
    <Typography sx={{ color: "secondary.main", fontSize: 11, fontWeight: 900, letterSpacing: ".1em" }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.5, fontSize: "2rem", fontWeight: 900 }}>
      {value.rate ? percent(value.rate.mean) : "No evidence"}
    </Typography>
    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
      {value.successes} successes / {value.games} qualifying games
      {value.rate
        ? ` · 95% interval ${percent(value.rate.interval.low)}–${percent(value.rate.interval.high)}`
        : ""}. {detail}
    </Typography>
  </Box>
);

const Section = ({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) => (
  <Box
    component="section"
    sx={{
      mt: { xs: 6, md: 9 },
      pt: { xs: 3, md: 4 },
      minWidth: 0,
      borderTop: "2px solid",
      borderColor: "primary.main",
      display: { md: "grid" },
      gridTemplateColumns: "minmax(180px,.7fr) minmax(0,2fr)",
      gap: { xs: 2, md: 6 },
    }}
  >
    <Box>
      <Typography
        sx={{
          color: "secondary.main",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: ".14em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        component="h2"
        sx={{
          mt: 1,
          fontSize: { xs: "2rem", md: "3rem" },
          fontWeight: 900,
          letterSpacing: "-.055em",
          lineHeight: 0.95,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ minWidth: 0, overflow: "hidden" }}>{children}</Box>
  </Box>
);

const TrendsEvidence = ({ data }: { data: PlayerTrendsResponse }) => {
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: layoutTokens.screenMinHeight,
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        px: layoutTokens.workspacePagePadding,
        py: { xs: 4, md: 7 },
        overflowX: "clip",
      }}
    >
      <Typography
        sx={{
          color: "secondary.main",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: ".16em",
          textTransform: "uppercase",
        }}
      >
        Observed performance · {data.scope.format}
      </Typography>
      <Typography
        component="h1"
        sx={{
          mt: 1,
          maxWidth: "10ch",
          fontSize: { xs: "3.5rem", sm: "5.5rem", lg: "7rem" },
          fontWeight: 900,
          letterSpacing: "-.075em",
          lineHeight: 0.82,
        }}
      >
        Are you improving?
      </Typography>
      <Typography
        sx={{
          mt: 2.5,
          maxWidth: 760,
          color: "text.secondary",
          fontSize: { xs: "1rem", sm: "1.15rem" },
          lineHeight: 1.6,
        }}
      >
        Only owned games inside this format and time range. Hover or touch charts for exact values; zoom rating and
        accuracy with Shift + wheel or the chart controls. Direction appears only when sample size and uncertainty
        support it.
      </Typography>
      <Section eyebrow="01 · Performance" title="Rating and results">
        <TrendSignal
          trend={data.performance.rating}
          unit="rating points"
          valueLabel="Rating"
          forecast={data.forecast.rating}
        />
        {data.performance.scoreVersusExpectation.status === "insufficient_data" ? (
          <Typography sx={{ mt: 3, color: "text.secondary" }}>
            {reason[data.performance.scoreVersusExpectation.reason]} Current sample:{" "}
            {data.performance.scoreVersusExpectation.games} games.
          </Typography>
        ) : (
          <ResultsExpectationSignal score={data.performance.scoreVersusExpectation} />
        )}
        <OpponentChallengeSignal bands={data.performance.challengeMix ?? []} />
      </Section>
      <Section eyebrow="02 · Calculation" title="Decision quality">
        <TrendSignal
          trend={data.calculation.accuracy}
          unit="accuracy points"
          valueLabel="Accuracy"
          forecast={data.forecast.accuracy}
        />
        <Box
          sx={{ mt: 4, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1.5 }}
        >
          <OutcomeRateSignal
            label="CONVERSION"
            value={data.calculation.conversion}
            detail="A qualifying game reached at least +1.50 or a forced mate on your move; success means the final result was a win."
          />
          <OutcomeRateSignal
            label="SAVES"
            value={data.calculation.saves}
            detail="A qualifying game reached at most −1.50 or you faced mate on your move; success means the final result was not a loss."
          />
        </Box>
        <MissedConversionList games={data.calculation.missedConversions ?? []} />
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ mb: 1, fontWeight: 900 }}>Clean-game change</Typography>
          {data.calculation.cleanGameChange.status === "observed" ? (
            <Typography sx={{ color: "text.secondary" }}>
              Earlier 10: {percent(data.calculation.cleanGameChange.prior.mean)} · recent 10:{" "}
              {percent(data.calculation.cleanGameChange.recent.mean)} ·{" "}
              {data.calculation.cleanGameChange.direction[0].toUpperCase()}
              {data.calculation.cleanGameChange.direction.slice(1)}
              {trendDirectionGlyph(data.calculation.cleanGameChange.direction)
                ? ` ${trendDirectionGlyph(data.calculation.cleanGameChange.direction)}`
                : ""}{" "}
              · {percent(data.calculation.cleanGameChange.probabilityImprovement)} probability of improvement. Clean
              means no classified error among measured player decisions.
            </Typography>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              {data.calculation.cleanGameChange.games === 10
                ? "Baseline established."
                : `${data.calculation.cleanGameChange.games} compatible games measured.`}{" "}
              {data.calculation.cleanGameChange.neededGames} more needed for recent-vs-previous comparison.
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ mb: 1, fontWeight: 900 }}>Error rate over time</Typography>
          <RateSignal trend={data.calculation.errorRate} />
        </Box>
        <Box sx={{ mt: 5 }}>
          <Typography sx={{ mb: 1, fontWeight: 900 }}>Where errors occur</Typography>
          <PhaseDecisionSignal phases={data.calculation.phaseErrorRates ?? []} />
        </Box>
      </Section>
      <Section eyebrow="03 · Clock" title="Decisions under pressure">
        <RateSignal trend={data.clock.pressuredErrorRate} label="Pressured error rate" />
        {data.clock.timeUse && (
          <Box sx={{ mt: 5 }}>
            <Typography sx={{ mb: 1, fontWeight: 900 }}>Where your clock goes</Typography>
            <TimeUseSignal timeUse={data.clock.timeUse} />
          </Box>
        )}
      </Section>
      <Section eyebrow="04 · Rhythm" title="How often you play">
        <ActivityRhythm series={data.performance.rating.series} />
        <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 12 }}>
          Active UTC weeks inside this scope. Intensity is games played—not strength or improvement.
        </Typography>
      </Section>
      <Section eyebrow="05 · Repertoire" title="What you are playing">
        {data.openings.length ? (
          <>
            <ChesslaChart
              kind="repertoire-treemap"
              label="Opening repertoire sized by games played."
              option={buildRepertoireOption(data.openings)}
              height={360}
            />
            <Box sx={{ mt: 2, display: "grid", gap: 0.75 }}>
              {data.openings.map((opening) => (
                <Box key={`${opening.color}:${opening.opening}`}>
                  <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    <Box component="span" sx={{ color: "primary.main", fontWeight: 900 }}>
                      {opening.color.toUpperCase()} · {opening.opening}
                    </Box>{" "}
                    · {opening.games} games · {percent(opening.score)} score · {opening.analyzedGames} analyzed ·{" "}
                    {opening.errors}/{opening.decisions} classified errors
                    {opening.errorRate
                      ? ` · ${percent(opening.errorRate.mean)} posterior rate (${percent(opening.errorRate.interval.low)}–${percent(opening.errorRate.interval.high)})`
                      : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 12 }}>
              Area is frequency, never strength or causation. White and Black repertoires remain distinct.
            </Typography>
            {data.openings.some((opening) => opening.bookExit) && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ mb: 1, fontWeight: 900 }}>Where you leave opening theory</Typography>
                <Box sx={cardGridSx}>
                  {data.openings.flatMap((opening) =>
                    opening.bookExit
                      ? [
                          <BookExitCard
                            key={`${opening.color}:${opening.opening}`}
                            opening={opening.opening}
                            color={opening.color}
                            exit={opening.bookExit}
                          />,
                        ]
                      : [],
                  )}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ color: "text.secondary" }}>No openings in this scope.</Typography>
        )}
      </Section>
      <Typography
        sx={{ mt: 6, pt: 2, borderTop: "1px solid", borderColor: "divider", color: "text.secondary", fontSize: 12 }}
      >
        Source: {data.provenance.games} owned games · {data.provenance.analyzedGames} compatible analyses ·
        Beta-Binomial rates and weighted least-squares slopes.
      </Typography>
    </Box>
  );
};

const TrendFormatSection = ({
  format,
  exactTimeControl,
  embedded,
}: {
  format: PerformanceFormat;
  exactTimeControl?: string;
  embedded: boolean;
}) => {
  const { data, error, loading } = useTrends({ format, exactTimeControl });
  if (loading)
    return (
      <ChesslaLoadingMark
        fillContent={!embedded}
        page={`${format} trends`}
        label={`Reading ${format} performance evidence`}
      />
    );
  if (error || !data)
    return (
      <Box role="alert" sx={{ maxWidth: layoutTokens.workspaceMaxWidth, mx: "auto", p: 4 }}>
        <Typography sx={{ fontWeight: 900 }}>{format.toUpperCase()} trends could not be loaded</Typography>
        <Typography sx={{ color: "text.secondary" }}>{error}</Typography>
      </Box>
    );
  return <TrendsEvidence data={data} />;
};

export const TrendsPage = () => {
  const performance = usePerformanceSelection(undefined, { page: "trends" });
  if (performance.loading)
    return <ChesslaLoadingMark fillContent page="Trends" label="Reading your analyzed game library" />;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          px: layoutTokens.workspacePagePadding,
          pt: { xs: 2, md: 3 },
        }}
      >
        {performance.error ? (
          <Typography role="alert" sx={{ color: "secondary.main" }}>
            {performance.error}
          </Typography>
        ) : performance.empty ? (
          <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography sx={{ fontWeight: 900 }}>Analyze a game to unlock Trends.</Typography>
            <Button component={RouterLink} to="/analyze" variant="contained" sx={{ mt: 2 }}>
              Analyze a game
            </Button>
          </Paper>
        ) : (
          performance.selection && (
            <PerformanceSelection
              tourPage="trends"
              selection={performance.selection}
              formats={performance.formats}
              clocks={performance.clocks}
              resultsLabel="Trends"
              onEdit={performance.edit}
              onFormat={performance.selectFormat}
              onClock={performance.selectClock}
            >
              {performance.showResults &&
                performance.selectedFormats.map((format) => (
                  <Box component="section" key={format} aria-label={`${format} trends`}>
                    <TrendFormatSection
                      format={format}
                      exactTimeControl={
                        performance.query?.format === format ? performance.query.exactTimeControl : undefined
                      }
                      embedded={performance.selectedFormats.length > 1}
                    />
                  </Box>
                ))}
            </PerformanceSelection>
          )
        )}
      </Box>
    </Box>
  );
};
